import { useCallback, useEffect, useRef, useState } from 'react';

import { ConfirmModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  usePreviewGradeChangeMutation,
  type GradeChangePreview,
  type ProfileFields,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

const STRINGS = appStrings.modules.profile.gradeChangeConfirm;

/**
 * The fields worth previewing. An enrollment type on its own can't lower a
 * grade, and mid-edit it reads as a change the student never made.
 */
export const GRADE_INPUT_PROFILE_FIELDS: ReadonlySet<string> = new Set([
  'academicHighSchoolExpectedGraduationYear',
  'academicHighSchoolProgramStartYear',
  'academicPostSecondaryExpectedGraduationYear',
  'academicPostSecondaryProgramStartYear',
]);

type ProfileFieldPatch = Partial<Record<ProfileFields, number | string | null>>;

// Only one of the two can be missing — a preview with neither is not a change,
// and never asks for confirmation.
const bodyFor = ({ kind, currentGrade, newGrade }: GradeChangePreview) => {
  if (kind === 'not-started') return STRINGS.body.notStarted;
  if (!newGrade) return STRINGS.body.finished(currentGrade ?? '');
  if (!currentGrade) return STRINGS.body.set(newGrade);

  return STRINGS.body.changed(currentGrade, newGrade);
};

/**
 * Asks a student to confirm an academic edit that would move their year in
 * school, since it also moves where their past assessments sit on the timeline.
 *
 * `confirmGradeChange` resolves true when the edit should go ahead — either
 * because it doesn't move the grade, or because the student said it's right.
 * A failed preview resolves true rather than blocking the edit behind a check
 * that couldn't run.
 */
export const useGradeChangeConfirm = () => {
  const [previewGradeChange] = usePreviewGradeChangeMutation();
  const [preview, setPreview] = useState<GradeChangePreview | null>(null);
  const decide = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    decide.current?.(confirmed);
    decide.current = null;
    setPreview(null);
  }, []);

  // An unanswered prompt still has a save waiting on it, so anything that takes
  // the prompt away — a second edit, or leaving the page — answers it with no.
  useEffect(() => () => decide.current?.(false), []);

  const confirmGradeChange = useCallback(
    async (fields: ProfileFieldPatch) => {
      let result: GradeChangePreview;

      try {
        result = await previewGradeChange(fields).unwrap();
      } catch {
        return true;
      }

      if (!result.requiresConfirmation) return true;

      decide.current?.(false);
      setPreview(result);

      return new Promise<boolean>((resolve) => {
        decide.current = resolve;
      });
    },
    [previewGradeChange],
  );

  const gradeChangeModal = preview ? (
    <ConfirmModal
      open
      variant="warning"
      onClose={() => settle(false)}
      headerText={preview.kind === 'not-started' ? STRINGS.notStartedHeader : STRINGS.header}
      bodyText={bodyFor(preview)}
      primaryAction={{
        label: STRINGS.confirm,
        onClick: () => settle(true),
        id: 'grade-change-confirm-btn',
      }}
      secondaryAction={{
        label: STRINGS.cancel,
        onClick: () => settle(false),
        id: 'grade-change-cancel-btn',
      }}
    />
  ) : null;

  return { confirmGradeChange, gradeChangeModal };
};
